import { createSelector } from '@reduxjs/toolkit'
import { mapColumns } from './columns'
import { isEmpty, flatten, cloneDeep } from 'lodash'
import { COLUMN_CATEGORY, COLUMN_I18N_MAPPING } from './recordings.constants'
import { PERMISSIONS } from '../../Permissions'
import { parseFilterData } from './helpers'
import {
  getFromDesktopProperties,
  getWillDisplayContactInUserTimezone,
  getUserTimezone,
} from '../../Common/DesktopProperties/desktopProperties.selectors'

/* Selector mappers */
export const filterPermittedColumns = (columnData, hasPermission) => {
  const hasMetadataPermission = hasPermission(PERMISSIONS.VIEW_CUSTOM_METADATA)
  return columnData.filter(category => hasMetadataPermission || category.category !== COLUMN_CATEGORY.METADATA)
}

export const extractColumnData = columnsObj => (columnsObj && columnsObj.columnData ? columnsObj.columnData : [])

export const columnsToArray = columnData =>
  columnData
    ? flatten(columnData.map(datum => datum.columns.map(column => ({ ...column, ...{ category: datum.category } }))))
    : []

// Convert column data from the API to a format consumable by ContactGrid:
export const tableFormatColumns = columns =>
  columns
    .filter(column => column.isSelected === true)
    .map(column => ({
      ...column,
      // This is to solve key errors from columns with duplicate IDs (metadata columns
      id: `${column.category}_${column.key}`,
      // These are specifically for the table
      label: column.category === COLUMN_CATEGORY.METADATA ? column.name : COLUMN_I18N_MAPPING[column.key] || column.key,
      width: column.width ? column.width : 200,
      disablePadding: false,
    }))
    .sort((a, b) => a.order - b.order)

export const getTableFormattedUserPrefsColumnsMapper = columns =>
  tableFormatColumns(columnsToArray(extractColumnData(columns)))

export const getTableFormattedAllColumnsMapper = columns =>
  tableFormatColumns(columnsToArray(extractColumnData(columns)))

export const contactsToColumnsMapper = (columns, contacts, willUseUserTimezone = false, userTimezone = false) => {
  const tableFormattedColumns = getTableFormattedUserPrefsColumnsMapper(columns)
  return contacts.map(contact =>
    mapColumns(tableFormattedColumns, contact, willUseUserTimezone && userTimezone, userTimezone),
  )
}

// For each column, use the retrieved width, otherwise default the width
export const getTableFormattedColumnsWithWidthsMapper = (columns, customWidths) =>
  getTableFormattedUserPrefsColumnsMapper(columns).map(column => {
    const customWidth = customWidths[column.name]
    column.width = customWidth ? customWidth : column.width
    return column
  })

const getColumnsOrDefault = (columns, defaultColumns) => !columns || isEmpty(columns.columnData) || !columns.columnData.length ? defaultColumns : setMetadataNames(columns, defaultColumns)

const setMetadataNames = (uiColumns, defaultColumns) => {
  const columns = { ...uiColumns }
  try {
    const findMetadataCategory = element => element.category === 'metadata_fields'
    const metadataIndexUI = columns.columnData.findIndex(findMetadataCategory)
    const metadataIndexDefault = defaultColumns.columnData.findIndex(findMetadataCategory)

    const uiMetadataColumns = columns.columnData[metadataIndexUI] ? columns.columnData[metadataIndexUI].columns : []
    const metadataColumns = defaultColumns.columnData[metadataIndexDefault] ? defaultColumns.columnData[metadataIndexDefault].columns : []

    uiMetadataColumns.forEach(uiColumn => {
      metadataColumns.forEach(defaultColumn => {
        if (defaultColumn.key === uiColumn.key) {
          uiColumn.name = defaultColumn.name
        }
      })
    })
  } catch (e) {
    // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
    console.error('Error in recordings.selectors:setMetadataNames', e)
  }
  return columns
}

/* Pure Selectors */
export const getUserPrefsColumns = ({
  userPrefsColumns = { columnData: [] },
  allColumns = [],
  metadataFields = [],
}) => {
  const metadataColumns = metadataFieldsToColumnsMapper(metadataFields)
  const defaultColumnData = [...allColumns, ...metadataColumns]

  return cloneDeep(getColumnsOrDefault(userPrefsColumns, { columnData: defaultColumnData }))
}

const metadataFieldsToColumnsMapper = metadataFields => [
  metadataFields.reduce(
    (acc, field, index) => {
      acc.columns.push({
        id: 10000 + index, // id is likely a meaningless property
        isEnabled: true,
        isSelected: false,
        key: field.key,
        name: field.name,
        order: 10000 + index,
        width: 0,
      })
      return acc
    },
    { category: COLUMN_CATEGORY.METADATA, columns: [] },
  ),
]

const combineColumnData = sources => {
  const map = new Map()

  // iterate through userPrefColumns, allColumns, and then metadataColumns
  sources.forEach(source => {
    // iterate through categories
    source.forEach(({ category, columns }) => {
      // see if the result already has the category, add it if not
      if (!map.has(category)) {
        map.set(category, [])
      }

      // the array of columns concerning the category
      const categoryColumns = map.get(category)

      // iterate through columns
      columns.forEach(column => {
        // if we haven't already added the column, add it
        if (!categoryColumns.find(x => x.key === column.key)) {
          categoryColumns.push(column)
        }
      })
    })
  })

  // transform map into columnData object
  return Array.from(map.keys()).map(category => ({
    category,
    columns: map.get(category),
  }))
}

// Uses props in selector (see: https://github.com/reduxjs/reselect/blob/master/README.md#accessing-react-props-in-selectors )
export const getPermittedColumns = (
  { userPrefsColumns = { columnData: [] }, allColumns = [], metadataFields = [] },
  { authenticatedUser: { hasPermission } },
) => {
  const metadataColumns = metadataFieldsToColumnsMapper(metadataFields)
  const defaultColumnData = [...allColumns, ...metadataColumns]
  const columnsOrDefault = getColumnsOrDefault(userPrefsColumns, { columnData: defaultColumnData })

  return {
    ...columnsOrDefault,
    columnData: filterPermittedColumns(extractColumnData(columnsOrDefault), hasPermission),
  }
}

//Performs selector on recordings state from root.
export const getFromRecordings = selector => ({ recordings }, ...args) => selector(recordings, ...args)

export const getAllColumns = ({ allColumns = [] }) => allColumns

// Uses props in selector (see: https://github.com/reduxjs/reselect/blob/master/README.md#accessing-react-props-in-selectors )
export const getPermittedAvailableColumns = (
  { userPrefsColumns = { columnData: [] }, allColumns = [], metadataFields = [] },
  { authenticatedUser: { hasPermission } },
) => {
  const metadataColumns = metadataFieldsToColumnsMapper(metadataFields)
  const defaultColumnData = [...allColumns, ...metadataColumns]
  const columnsOrDefault = getColumnsOrDefault(userPrefsColumns, { columnData: defaultColumnData })

  const columnData = filterPermittedColumns(
    combineColumnData([extractColumnData(columnsOrDefault), deselectColumns(defaultColumnData)]),
    hasPermission,
  )

  return {
    columnData,
  }
}

export const deselectColumns = source =>
  source.map(({ category, columns }) => ({
    category,
    columns: columns.map(column => ({
      ...column,
      isSelected: false,
    })),
  }))

export const getContacts = ({ contacts }) => contacts

export const getPageParams = ({ pageParams }) => pageParams

const getActiveFilters = ({ activeFilters }) =>
  isEmpty(activeFilters)
    ? {
      range: 'date_range_today',
      searchScope: 'allEvaluations',
    }
    : activeFilters

const getCustomColumnWidths = ({ customColumnWidths }) => customColumnWidths

/* Memoized Selectors */
export const getTableFormattedColumns = createSelector(getUserPrefsColumns, getTableFormattedUserPrefsColumnsMapper)

// Makes new selector instance (see: https://github.com/reduxjs/reselect/blob/master/README.md#accessing-react-props-in-selectors )
export const makeGetTableFormattedPermittedColumns = () =>
  createSelector(getPermittedColumns, getTableFormattedUserPrefsColumnsMapper)

export const getTableFormattedAvailableColumns = createSelector(getAllColumns, getTableFormattedAllColumnsMapper)

// Makes new selector instance (see: https://github.com/reduxjs/reselect/blob/master/README.md#accessing-react-props-in-selectors )
export const makeGetTableFormattedPermittedAvailableColumns = () =>
  createSelector(getPermittedAvailableColumns, getTableFormattedAllColumnsMapper)

export const getTableFormattedColumnsWithWidths = createSelector(
  [getUserPrefsColumns, getCustomColumnWidths],
  getTableFormattedColumnsWithWidthsMapper,
)

// Makes new selector instance (see: https://github.com/reduxjs/reselect/blob/master/README.md#accessing-react-props-in-selectors )
export const makeGetTableFormattedPermittedColumnsWithWidths = () =>
  createSelector([getPermittedColumns, getCustomColumnWidths], getTableFormattedColumnsWithWidthsMapper)

//TODO figure out how to use a timezone with this, it is wrong right now
// Convert contact data from the API to a format consumable by ContactGrid: it should be an array where each index corresponds to the columns position
// Makes new selector instance (see: https://github.com/reduxjs/reselect/blob/master/README.md#accessing-react-props-in-selectors )
export const makeGetTableFormattedContactsWithPermittedColumns = () =>
  createSelector(
    [
      getFromRecordings(getPermittedColumns),
      getFromRecordings(getContacts),
      getFromDesktopProperties(getWillDisplayContactInUserTimezone),
      getUserTimezone,
    ],
    contactsToColumnsMapper,
  )

// Convert Saved Search filters
export const getActiveSearchFilters = createSelector([getActiveFilters], filters => parseFilterData(filters))
